import { t, translate } from '@avalara/bat-apps-shared/i18n';
import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Link, useHistory } from 'react-router-dom';
import { formatPrice } from '../../../shared/helpers';
import conf from '../../conf';

import { setSelectedPaymentMethod } from '../../lib/actions';
import { getTotalCost } from '../../lib/buy-flow';
import { addPaidAvataxTier } from '../../lib/side-effects/offers';
import { selectState } from '../../selectors';
import { selectPaymentMethodSelectionInfo } from '../../selectors/account';

// Components
import CartDisclaimerText from '../cart-disclaimer-text';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { purchaseDeflectionWording } from '../helpers';
import { getRenewalDate } from '../helpers/buy-flow';
import { getSalesTax } from '../helpers/pricing';
// helpers
import { catchNextClick } from '../helpers/sign-up';
import PaymentMethodSelector from '../shared/payment/payment-method-selector';
import { HelpContainer } from '../styled/components/buy-flow';

import {
  BuyNowButtonContainer,
  CartActivationFee,
  CartContainer,
  CartImagePrice,
  CartInformation,
  CartPlanDetails,
  CartRenewalDetails,
  DeflectionWording,
  LogoText,
  PlanContainer,
  SalesTaxContainer,
  TotalPriceContainer,
  TotalsContainer,
} from '../styled/components/cart';

const getDeflectionWording = (availableForOnlineBuy: boolean) => (
  <DeflectionWording>
    {availableForOnlineBuy === true ? (
      purchaseDeflectionWording
    ) : (
      <span data-ref='buy.buy-flow.cart.unable-to-purchase-text'>
        {"This connector can't be purchased online. Please call "}
        <a href='tel://1-877-224-3650'>1-877-224-3650</a>
        {" if you're interested."}
      </span>
    )}
  </DeflectionWording>
);

const CartComponent = ({
  isPaymentPage,
}: {
  isPaymentPage?: boolean;
}) => {
  const history = useHistory();
  const { pathname, search } = useLocation();
  const dispatch = useAppDispatch();
  const {
    account: { accountId, isAdmin, isLoggedIn, connector: selectedConnector, paymentMethods, selectedPaymentMethod },
    buyFlow,
  } = useSelector(selectState);
  const { selectedTier, salestax } = buyFlow;

  const paymentMethodSelectionInfo = useSelector(selectPaymentMethodSelectionInfo);

  const disabled =
    Boolean((accountId !== null && localStorage.getItem(`paidAvataxOrBundlePurchase-${accountId}`)) || !selectedTier) ||
    (isLoggedIn && isAdmin === false);

  const totalCost = getTotalCost(buyFlow);

  const selectedTierPricingText =
    selectedTier && selectedTier.recurringPrice === 0 ? 'Free' : formatPrice(selectedTier?.recurringPrice || 0, 2);

  const onSetPaymentMethod = (paymentMethodId: string | null) => dispatch(setSelectedPaymentMethod(paymentMethodId));

  return (
    <CartContainer>
      <HelpContainer>
        <h3 className='alt'>{t('client.headings.need-help-with-this')}</h3>
        <p>
          {t('client.general.call-us-at')}{' '}
          <a href={'tel://(877) 224-3650'} style={{ color: 'var(--color-blue-darker)' }}>
            (877) 224-3650
          </a>
        </p>
      </HelpContainer>
      <CartInformation id='cart'>
        <PlanContainer>
          <CartImagePrice offsetRight={40} doNotRespond>
            <div>
              <img
                src={`${conf.ASSET_PATH}/assets/images/avalara-avatax-icon-cart.svg`}
                alt={t('client.general.avalara-avatax')}
              />
              <LogoText>{t('client.general.avalara-avatax')}</LogoText>
            </div>
            {!disabled ? <p data-ref='buy.buy-flow.cart.selected-tier-price'>{selectedTierPricingText}</p> : <p />}
          </CartImagePrice>
          <CartPlanDetails className={!disabled ? 'active' : ''}>
            {!disabled ? (
              <Fragment>
                {translate('client.general.up-to-transactions', {
                  quantity: selectedTier?.quantity ?? '',
                  quantityValue: (chunks) => <span className='plan-details'>{chunks}</span>,
                })}
              </Fragment>
            ) : (
              <span className='plan-details'>{t('client.general.please-select-a-plan')}</span>
            )}
          </CartPlanDetails>
          {!disabled && (
            <Fragment>
              <CartRenewalDetails>{getRenewalDate(new Date())}</CartRenewalDetails>
              <CartActivationFee>
                {t('client.general.one-time-activation-fee')}
                <span data-ref='buy.buy-flow.cart.activation-fee' className='tier-wave'>
                  {selectedTier?.waived && 'FREE'}
                </span>
                <span className='activation-price'>
                  <span className={`activation-price ${selectedTier?.waived ? ' text-strikethrough' : ''}`}>
                    {formatPrice(selectedTier?.activationPrice || 0)}
                  </span>
                </span>
              </CartActivationFee>
              <TotalPriceContainer>
                <SalesTaxContainer offsetLeft={60} half doNotRespond>
                  <p>{t('client.general.sales-tax')}:</p>
                  <p id='salestax' className={salestax !== null ? 'disable' : ''}>
                    {selectedTier && getSalesTax(selectedTier, salestax)}
                  </p>
                </SalesTaxContainer>
                <TotalsContainer offsetLeft={60} half doNotRespond>
                  <p>{t('client.general.total')}:</p>
                  <p id={'total-cost'}>{formatPrice(totalCost)}</p>
                </TotalsContainer>
              </TotalPriceContainer>
              {paymentMethods.length && !isPaymentPage ? (
                <PaymentMethodSelector
                  paymentMethodSelectionInfo={paymentMethodSelectionInfo}
                  onSetPaymentMethod={onSetPaymentMethod}
                />
              ) : null}
            </Fragment>
          )}
        </PlanContainer>
        <BuyNowButtonContainer>
          {(isLoggedIn && !isAdmin) || !selectedConnector.availableForOnlineBuy ? (
            getDeflectionWording(selectedConnector.availableForOnlineBuy)
          ) : (
            <Fragment>
              <CartDisclaimerText
                isDisabled={disabled}
                buttonLabel={
                  selectedPaymentMethod ? t('client.buttons.buy-now') : t('client.buttons.continue-to-payment')
                }
              />
              {!isPaymentPage && !selectedPaymentMethod && (
                <Link to={`${pathname}/payment${search}`} onClick={(e) => catchNextClick(e, disabled)}>
                  <button
                    type='button'
                    className='continue primary large'
                    data-ref='buy.buy-flow.cart.continue-to-payment-button'
                    disabled={disabled}
                  >
                    {t('client.buttons.continue-to-payment')}
                  </button>
                </Link>
              )}
              {!isPaymentPage && selectedPaymentMethod && (
                <button
                  id='submit-payment'
                  type='button'
                  data-ref='buy.buy-flow.cart.buy-now-button'
                  onClick={() => dispatch(addPaidAvataxTier({ history, paymentToken: selectedPaymentMethod.Id }))}
                  className='default primary large'
                  disabled={disabled}
                >
                  {t('client.buttons.buy-now')}
                </button>
              )}
            </Fragment>
          )}
        </BuyNowButtonContainer>
      </CartInformation>
    </CartContainer>
  );
};

export default CartComponent;
